import type { StorageAdapter } from './types'
import {
  LEGACY_LOCAL_STORAGE_PREFIX,
  LEGACY_METADATA_KEY,
  STORAGE_METADATA_KEY,
  chapterBackupKey,
  parseLegacyLocalStorageKey,
  type ChapterBackupRecord,
  type StorageMetadata,
} from './types'

function toRecordFromLegacy(
  legacy: Partial<ChapterBackupRecord>,
  parsed: { projectId: string; chapterId: string },
): ChapterBackupRecord {
  return {
    projectId: legacy.projectId || parsed.projectId,
    chapterId: legacy.chapterId || parsed.chapterId,
    title: legacy.title || '',
    content: legacy.content,
    jsonContent: legacy.jsonContent || {},
    timestamp: typeof legacy.timestamp === 'number' ? legacy.timestamp : Date.now(),
    savedAt: legacy.savedAt || new Date().toISOString(),
    sourceId: legacy.sourceId || crypto.randomUUID(),
    baseUpdatedAt: typeof legacy.baseUpdatedAt === 'string' ? legacy.baseUpdatedAt : undefined,
    userId: typeof legacy.userId === 'string' ? legacy.userId : undefined,
  }
}

/**
 * One-time migration of legacy localStorage chapter backups into the storage adapter.
 * Idempotent: remaining legacy keys are moved on each call until none remain.
 */
export async function migrateLocalStorageBackups(adapter: StorageAdapter): Promise<number> {
  if (typeof localStorage === 'undefined') return 0

  let migrated = 0

  try {
    const keys = Object.keys(localStorage).filter(
      (key) => key.startsWith(`${LEGACY_LOCAL_STORAGE_PREFIX}_`) && key !== LEGACY_METADATA_KEY,
    )

    const outcomes = await Promise.all(
      keys.map(async (key) => {
        const parsed = parseLegacyLocalStorageKey(key)
        if (!parsed) return false

        const raw = localStorage.getItem(key)
        if (!raw) {
          localStorage.removeItem(key)
          return false
        }

        try {
          const legacy = JSON.parse(raw) as Partial<ChapterBackupRecord>
          const record = toRecordFromLegacy(legacy, parsed)
          const storageKey = chapterBackupKey(record.projectId, record.chapterId)
          const existing = await adapter.get<ChapterBackupRecord>(storageKey)

          // Keep the newer backup when both localStorage and IndexedDB have data.
          if (!existing || record.timestamp >= existing.timestamp) {
            await adapter.set(storageKey, record)
          }

          localStorage.removeItem(key)
          return true
        } catch (error) {
          console.error('Error migrating localStorage backup:', error)
          localStorage.removeItem(key)
          return false
        }
      }),
    )

    migrated = outcomes.filter(Boolean).length

    // Drop legacy metadata key; adapter metadata replaces it.
    localStorage.removeItem(LEGACY_METADATA_KEY)

    const metadata =
      (await adapter.get<StorageMetadata>(STORAGE_METADATA_KEY)) ||
      ({
        lastCleanup: 0,
        version: '2.0',
      } satisfies StorageMetadata)

    metadata.localStorageMigrated = true
    metadata.version = metadata.version || '2.0'
    await adapter.set(STORAGE_METADATA_KEY, metadata)
  } catch (error) {
    console.error('Error during localStorage backup migration:', error)
  }

  return migrated
}

/** Sync peek of remaining legacy localStorage backups (used before async hydrate). */
export function readLegacyLocalStorageBackups(): ChapterBackupRecord[] {
  if (typeof localStorage === 'undefined') return []

  try {
    return Object.keys(localStorage)
      .filter(
        (key) => key.startsWith(`${LEGACY_LOCAL_STORAGE_PREFIX}_`) && key !== LEGACY_METADATA_KEY,
      )
      .flatMap((key) => {
        const parsed = parseLegacyLocalStorageKey(key)
        if (!parsed) return []
        const raw = localStorage.getItem(key)
        if (!raw) return []
        try {
          const legacy = JSON.parse(raw) as Partial<ChapterBackupRecord>
          return [toRecordFromLegacy(legacy, parsed)]
        } catch {
          return []
        }
      })
  } catch {
    return []
  }
}

export function listLegacyLocalStorageKeys(): string[] {
  if (typeof localStorage === 'undefined') return []
  try {
    return Object.keys(localStorage).filter(
      (key) =>
        key.startsWith(LEGACY_LOCAL_STORAGE_PREFIX) &&
        (key === LEGACY_METADATA_KEY || key.startsWith(`${LEGACY_LOCAL_STORAGE_PREFIX}_`)),
    )
  } catch {
    return []
  }
}
